/**
 * MarkdownEditor — a lightweight toolbar-enhanced markdown editor.
 *
 * Not a full WYSIWYG rich-text engine (which would break the
 * downstream Markdown → PDF pipeline). Instead, this wraps a large
 * textarea with a toolbar that inserts / wraps markdown syntax around
 * the current selection, gives keyboard shortcuts for the common
 * operations, and applies typography styles (bigger font, comfortable
 * line-height, monospace only for inline code) so editing legal
 * documents feels like a proper writing surface rather than a config
 * text-area.
 *
 * Supported toolbar actions:
 *   • H1 / H2 / H3
 *   • Bold (Ctrl/Cmd+B) / Italic (Ctrl/Cmd+I) / Inline code
 *   • Bulleted list · Numbered list
 *   • Blockquote · Horizontal rule · Link
 *   • Insert 3×3 table skeleton
 */
import React, { useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Heading1, Heading2, Heading3, Bold, Italic, Code2,
  List, ListOrdered, Quote, Minus, Link2, Table as TableIcon,
} from "lucide-react";

function useToolbar(textareaRef, value, onChange) {
  const applyWrap = useCallback((before, after = before) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.slice(start, end);
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length,
                           start + before.length + selected.length);
    });
  }, [textareaRef, value, onChange]);

  const applyLinePrefix = useCallback((prefixFn) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    // Expand selection to full line(s)
    const before = value.slice(0, start);
    const lineStart = before.lastIndexOf("\n") + 1;
    const after = value.slice(end);
    const restOfLastLine = after.indexOf("\n");
    const lineEnd = end + (restOfLastLine < 0 ? after.length : restOfLastLine);
    const block = value.slice(lineStart, lineEnd);
    const rebuilt = block
      .split("\n")
      .map((ln, i) => prefixFn(ln, i))
      .join("\n");
    const next = value.slice(0, lineStart) + rebuilt + value.slice(lineEnd);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(lineStart, lineStart + rebuilt.length);
    });
  }, [textareaRef, value, onChange]);

  const insertAtCursor = useCallback((snippet) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    // Ensure the snippet lands on its own paragraph
    const needsLeadingNl = start > 0 && value[start - 1] !== "\n";
    const finalSnippet = (needsLeadingNl ? "\n" : "") + snippet + "\n";
    const next = value.slice(0, start) + finalSnippet + value.slice(start);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + finalSnippet.length;
      ta.setSelectionRange(pos, pos);
    });
  }, [textareaRef, value, onChange]);

  const linkify = useCallback(() => {
    const url = window.prompt("Link URL", "https://");
    if (!url) return;
    applyWrap("[", `](${url})`);
  }, [applyWrap]);

  return {
    h1: () => applyLinePrefix((ln) => `# ${ln.replace(/^#+\s*/, "")}`),
    h2: () => applyLinePrefix((ln) => `## ${ln.replace(/^#+\s*/, "")}`),
    h3: () => applyLinePrefix((ln) => `### ${ln.replace(/^#+\s*/, "")}`),
    bold:   () => applyWrap("**"),
    italic: () => applyWrap("*"),
    code:   () => applyWrap("`"),
    ul:     () => applyLinePrefix((ln) => ln.startsWith("- ") ? ln : `- ${ln}`),
    ol:     () => applyLinePrefix((ln, i) => `${i + 1}. ${ln.replace(/^\d+\.\s*/, "")}`),
    quote:  () => applyLinePrefix((ln) => ln.startsWith("> ") ? ln : `> ${ln}`),
    hr:     () => insertAtCursor("---"),
    link:   linkify,
    table:  () => insertAtCursor(
      "| Column A | Column B | Column C |\n" +
      "|---|---|---|\n" +
      "| … | … | … |\n" +
      "| … | … | … |"),
  };
}

export default function MarkdownEditor({ value = "", onChange, testid,
                                          minHeight = "70vh" }) {
  const ref = useRef(null);
  const t = useToolbar(ref, value, onChange);

  const onKey = (e) => {
    const meta = e.metaKey || e.ctrlKey;
    if (!meta) return;
    if (e.key === "b" || e.key === "B") { e.preventDefault(); t.bold(); }
    else if (e.key === "i" || e.key === "I") { e.preventDefault(); t.italic(); }
    else if (e.key === "k" || e.key === "K") { e.preventDefault(); t.link(); }
  };

  const btn = (label, Icon, onClick, testId) => (
    <Button type="button" size="sm" variant="ghost"
      className="h-7 w-7 p-0 text-zinc-600 hover:text-[#002FA7] hover:bg-[#002FA7]/10"
      onClick={onClick} title={label} data-testid={testId}>
      <Icon className="h-3.5 w-3.5" />
    </Button>
  );

  return (
    <div className="rounded-sm border border-zinc-300 bg-white overflow-hidden"
      data-testid={testid || "markdown-editor"}>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-zinc-200 bg-zinc-50 px-2 py-1">
        {btn("Heading 1", Heading1, t.h1, "md-h1")}
        {btn("Heading 2", Heading2, t.h2, "md-h2")}
        {btn("Heading 3", Heading3, t.h3, "md-h3")}
        <div className="w-px h-4 bg-zinc-300 mx-1" />
        {btn("Bold (Ctrl+B)", Bold, t.bold, "md-bold")}
        {btn("Italic (Ctrl+I)", Italic, t.italic, "md-italic")}
        {btn("Inline code", Code2, t.code, "md-code")}
        <div className="w-px h-4 bg-zinc-300 mx-1" />
        {btn("Bulleted list", List, t.ul, "md-ul")}
        {btn("Numbered list", ListOrdered, t.ol, "md-ol")}
        {btn("Blockquote", Quote, t.quote, "md-quote")}
        <div className="w-px h-4 bg-zinc-300 mx-1" />
        {btn("Insert table", TableIcon, t.table, "md-table")}
        {btn("Horizontal rule", Minus, t.hr, "md-hr")}
        {btn("Link (Ctrl+K)", Link2, t.link, "md-link")}
      </div>
      <textarea ref={ref} value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKey}
        spellCheck
        style={{ minHeight, tabSize: 2 }}
        className="w-full px-4 py-3 text-[13.5px] leading-[1.65] font-sans text-zinc-800
                   focus:outline-none border-0 resize-y"
        data-testid={`${testid || "markdown-editor"}-textarea`} />
    </div>
  );
}
